import React, { useState, useEffect } from 'react';
import './SystemSettings.css';
import { authFetch, API_BASE } from '../api/orchestrator';

interface SystemSetting {
  id: number;
  setting_key: string;
  setting_value: string;
  setting_type: string;
  category: string;
  display_name: string;
  description: string;
  min_value: number | null;
  max_value: number | null;
  requires_restart: boolean;
  updated_at: string;
}

interface WorkerInfo {
  workerId: string;
  url: string;
  assignedEntities: string[];
  sessionCount: number;
}

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, SystemSetting[]>>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [workerConfig, setWorkerConfig] = useState<any>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning', text: string } | null>(null);

  // API Keys state
  const [openRouterApiKey, setOpenRouterApiKey] = useState<string>('');
  const [editingApiKey, setEditingApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState<string>('');
  const [savingApiKey, setSavingApiKey] = useState(false);

  useEffect(() => {
    loadSettings();
    loadWorkerConfig();
    loadOpenRouterApiKey();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/system-settings`);
      const data = await response.json();
      
      if (data.ok) {
        setSettings(data.settings);
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to load system settings:', error);
      showMessage('error', 'Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkerConfig = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/worker-config`);
      const data = await response.json();

      if (data.ok) {
        setWorkerConfig(data.config);
      }
    } catch (error) {
      console.error('Failed to load worker config:', error);
    }
  };

  const loadOpenRouterApiKey = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/openrouter-api-key`);
      const data = await response.json();

      if (data.ok) {
        setOpenRouterApiKey(data.apiKey || '');
      }
    } catch (error) {
      console.error('Failed to load OpenRouter API key:', error);
    }
  };

  const handleEditApiKey = () => {
    setEditingApiKey(true);
    setApiKeyInput('');
  };

  const handleCancelApiKey = () => {
    setEditingApiKey(false);
    setApiKeyInput('');
  };

  const handleSaveApiKey = async () => {
    if (!apiKeyInput.trim()) {
      showMessage('error', 'Please enter an API key');
      return;
    }

    setSavingApiKey(true);
    try {
      const response = await authFetch(`${API_BASE}/api/admin/openrouter-api-key`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: apiKeyInput })
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'OpenRouter API key updated successfully');
        setEditingApiKey(false);
        setApiKeyInput('');
        await loadOpenRouterApiKey();
      } else {
        showMessage('error', data.error || 'Failed to update API key');
      }
    } catch (error) {
      console.error('Failed to update API key:', error);
      showMessage('error', 'Failed to update API key');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleEdit = (setting: SystemSetting) => {
    setEditingKey(setting.setting_key);
    setEditValue(setting.setting_value);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (setting: SystemSetting) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/admin/system-settings/${setting.setting_key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: editValue })
      });

      const data = await response.json();
      
      if (data.ok) {
        showMessage(
          data.requiresRestart ? 'warning' : 'success',
          data.message
        );
        setEditingKey(null);
        await loadSettings();
        await loadWorkerConfig();
      } else {
        showMessage('error', data.error || 'Failed to update setting');
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
      showMessage('error', 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'warning', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'workers': return '👷';
      case 'security': return '🔒';
      case 'performance': return '⚡';
      default: return '⚙️';
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (loading) {
    return <div className="system-settings-loading">Loading system settings...</div>;
  }

  return (
    <div className="system-settings">
      <div className="system-settings-header">
        <h2>⚙️ System Settings</h2>
        <p className="settings-description">
          Configure system-wide parameters. Settings marked with 🔄 require service restart to take effect.
        </p>
      </div>

      {message && (
        <div className={`settings-message settings-message-${message.type}`}>
          {message.type === 'success' && '✅ '}
          {message.type === 'error' && '❌ '}
          {message.type === 'warning' && '⚠️ '}
          {message.text}
        </div>
      )}

      {/* API Keys Section */}
      <div className="settings-category">
        <h3 className="category-header">
          <span className="category-icon">🔑</span>
          API Keys
        </h3>

        <div className="settings-list">
          <div className="setting-item">
            <div className="setting-header">
              <div className="setting-title">
                OpenRouter API Key
              </div>
              <div className="setting-actions">
                {editingApiKey ? (
                  <>
                    <button
                      className="btn-save"
                      onClick={handleSaveApiKey}
                      disabled={savingApiKey}
                    >
                      {savingApiKey ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={handleCancelApiKey}
                      disabled={savingApiKey}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={handleEditApiKey}
                  >
                    {openRouterApiKey ? 'Update' : 'Add'}
                  </button>
                )}
              </div>
            </div>

            <p className="setting-description">
              Your OpenRouter API key for accessing 350+ AI models. Get your key at{' '}
              <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">
                openrouter.ai/keys
              </a>
              . When configured, the AI model selector will show all available models.
            </p>

            <div className="setting-value-container">
              {editingApiKey ? (
                <input
                  type="password"
                  className="setting-input"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-or-v1-..."
                  disabled={savingApiKey}
                />
              ) : (
                <div className="setting-value">
                  {openRouterApiKey ? (
                    <strong>••••••••••••••••{openRouterApiKey.slice(-8)}</strong>
                  ) : (
                    <span style={{ color: '#888' }}>Not configured (using default free models)</span>
                  )}
                </div>
              )}
            </div>

            {openRouterApiKey && (
              <div className="setting-meta">
                <span className="setting-key">Custom API key configured ✅</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Worker Configuration Summary */}
      {workerConfig && (
        <div className="worker-config-summary">
          <h3>📊 Current Worker Configuration</h3>
          <div className="config-stats">
            <div className="stat-card">
              <div className="stat-label">Max Sessions Per Worker</div>
              <div className="stat-value">{workerConfig.maxSessionsPerWorker}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Workers</div>
              <div className="stat-value">{workerConfig.totalWorkers}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Capacity</div>
              <div className="stat-value">
                {workerConfig.maxSessionsPerWorker * workerConfig.totalWorkers} sessions
              </div>
            </div>
          </div>
          
          <div className="worker-list">
            <h4>Worker Assignments</h4>
            {workerConfig.workers.map((worker: WorkerInfo) => (
              <div key={worker.workerId} className="worker-item">
                <div className="worker-info">
                  <strong>{worker.workerId}</strong> ({worker.url})
                </div>
                <div className="worker-sessions">
                  {worker.sessionCount} / {workerConfig.maxSessionsPerWorker} sessions
                  {worker.assignedEntities && worker.assignedEntities.length > 0 && (
                    <span className="entity-list">
                      : {worker.assignedEntities.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings by Category */}
      {categories.map(category => (
        <div key={category} className="settings-category">
          <h3 className="category-header">
            <span className="category-icon">{getCategoryIcon(category)}</span>
            {getCategoryLabel(category)}
          </h3>

          <div className="settings-list">
            {settings[category]?.map(setting => (
              <div key={setting.id} className="setting-item">
                <div className="setting-header">
                  <div className="setting-title">
                    {setting.display_name}
                    {setting.requires_restart && (
                      <span className="restart-badge" title="Requires service restart">
                        🔄
                      </span>
                    )}
                  </div>
                  <div className="setting-actions">
                    {editingKey === setting.setting_key ? (
                      <>
                        <button
                          className="btn-save"
                          onClick={() => handleSave(setting)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(setting)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                <p className="setting-description">{setting.description}</p>

                <div className="setting-value-container">
                  {editingKey === setting.setting_key ? (
                    <input
                      type={setting.setting_type === 'number' ? 'number' : 'text'}
                      className="setting-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      min={setting.min_value || undefined}
                      max={setting.max_value || undefined}
                      disabled={saving}
                    />
                  ) : (
                    <div className="setting-value">
                      <strong>{setting.setting_value}</strong>
                      {setting.setting_type === 'number' && setting.min_value !== null && (
                        <span className="value-range">
                          (Range: {setting.min_value} - {setting.max_value})
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="setting-meta">
                  <span className="setting-key">{setting.setting_key}</span>
                  <span className="setting-updated">
                    Updated: {new Date(setting.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
